import React, { useCallback, useMemo } from 'react';
import { camelCase } from 'change-case';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { LegacyPermissions, usePermissions } from '#core/Permissions';
import productsConfig, { Page, PageConfig } from '#config/products';
import ProductsContext, {
  Product,
  ProductPage,
} from '../contexts/ProductsContext';
import { useInstitution } from '#core/Institution';
import { useYears } from '#core/Years';

const componentWithLegacyPermissions = (
  Component: Page['component'],
): Page['component'] => (props) => (
  <LegacyPermissions>
    <Component {...props} />
  </LegacyPermissions>
);

const leadingSlashRegex = /^\//;

const Products: React.FC<{}> = ({ children }) => {
  const { hasResourcePermissionNew } = usePermissions();

  const flags = useFlags();
  const productConf = [...productsConfig];

  const { institution } = useInstitution();
  const { availableYears } = useYears();

  const WUR_REPORTERS_INTERIM_ENABLED =
    flags[camelCase('wur-reporters-interim-enabled')];

  const SHOW_DOCUMENT_LIBRARY = flags[camelCase('showDocumentLibrary')];

  const SHOW_IFRAME_IMPACT_PAGES = flags[camelCase('showIframeImpactPages')];

  const SHOW_NON_IFRAME_IMPACT_PAGES =
    flags[camelCase('showNonIFrameImpactPages')];

  if (!WUR_REPORTERS_INTERIM_ENABLED) {
    productConf.forEach((config, index) => {
      if (config.authKey === 'wurReporterInterim') {
        productConf.splice(index, 1);
      }
    });
  }

  if (!SHOW_DOCUMENT_LIBRARY) {
    productConf.forEach((config, index) => {
      if (config.authKey === 'resources') {
        productConf.splice(index, 1);
      }
    });
  }

  // Filter Impact pages based on flags
  productConf.forEach((config) => {
    if (config.authKey === 'sdgImpact' && Array.isArray(config.pages)) {
      config.pages = config.pages.filter((page) => {
        const path = page.path || '';

        const isNonIframePage =
          (path.includes('overview') || path.includes('details')) &&
          !path.includes('-impact');

        const isIframePage =
          path.includes('overview-impact') || path.includes('details-impact');

        if (isIframePage && SHOW_IFRAME_IMPACT_PAGES) return true;
        if (isNonIframePage && SHOW_NON_IFRAME_IMPACT_PAGES) return true;

        // Fallback to return any other Impact pages
        return !isIframePage && !isNonIframePage;
      });
    }
  });

  const products = useMemo(
    () =>
      productConf
        .reduce<Product[]>((productAcc, product) => {
          const {
            authKey,
            usesLegacyPermissions,
            pages: pagesConfig,
            path: productPath,
            featureFlagId: productFlag,
            ...productConfig
          } = product;

          const pages = pagesConfig.reduce<ProductPage[]>(
            (
              pagesAcc,
              { component, path, featureFlagId: flag, ...productPage },
            ) => {
              if (flag && flags[camelCase(flag)] === false) {
                return pagesAcc;
              }
              const resourcePagePath = path.includes('/:')
                ? camelCase(path.substring(0, path.indexOf('/:')))
                : camelCase(path);

              let action: string;

              if (resourcePagePath === 'academicInsights') {
                action = 'reputation_plus:viewPage';
              } else {
                action = `${authKey}:viewPage`;
              }

              if (authKey === 'sdgImpact') {
                action = `${authKey}:sideBar:${resourcePagePath}`;
              }

              let userHasAccess = !authKey;

              if (resourcePagePath === 'resourcesHub') {
                userHasAccess = true;
              }

              availableYears[productPath].forEach(({ year }) => {
                userHasAccess =
                  userHasAccess ||
                  hasResourcePermissionNew({
                    action,
                    year,
                    institutionId: institution?.institutionId,
                  });
              });

              let newComponent: React.FC<{
                pageConfig: PageConfig;
              }>;

              if (productFlag && flags[camelCase(productFlag)] === false) {
                newComponent =
                  productConfig.disabledFeatureComponent ?? component;
              } else {
                newComponent = usesLegacyPermissions
                  ? componentWithLegacyPermissions(component)
                  : component;
              }

              const hasUserAccessToAcademicInsights =
                (authKey === 'reputation' &&
                  resourcePagePath !== 'academicInsights') ||
                userHasAccess;

              const allOtherProductsPages = pagesAcc;

              const acadamicInsightPages = [
                ...pagesAcc,
                {
                  ...productPage,
                  path,
                  routerPath: `${productPath}/${path}`,
                  url: `/datapoints/${productPath}/${path.replace(
                    leadingSlashRegex,
                    '',
                  )}`,
                  usesLegacyPermissions,
                  component: newComponent,
                  auth: {
                    action,
                    userHasAccess,
                  },
                },
              ];

              return hasUserAccessToAcademicInsights
                ? acadamicInsightPages
                : allOtherProductsPages;
            },
            [],
          );

          const userHasAccessToProduct = pages.reduce(
            (hasAccess, { auth: { userHasAccess: hasAccessToPage } }) =>
              hasAccess || hasAccessToPage,
            false,
          );

          return userHasAccessToProduct
            ? [
                ...productAcc,
                {
                  ...productConfig,
                  path: productPath,
                  authKey,
                  auth: {
                    userHasAccess: userHasAccessToProduct,
                  },
                  pages,
                },
              ]
            : productAcc;
        }, [])
        .sort(({ name: nameA }, { name: nameB }) => {
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          return 1;
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flags, hasResourcePermissionNew, availableYears],
  );

  const getProduct = useCallback(
    (productName: string) => {
      const { products: institutionProducts } = institution;
      return (institutionProducts || []).find(
        ({ name }) => name.toLowerCase() === productName.toLowerCase(),
      );
    },
    [institution],
  );

  const productGroups = useCallback(
    (productName: string) => {
      const product = getProduct(productName);
      const groups = product?.institutionGroups?.map(({ name }) => name);
      return groups || [];
    },
    [getProduct],
  );

  const productPeers = useCallback(
    (productName: string) => {
      const product = getProduct(productName);
      const group = product?.institutionGroups?.find(
        ({ name }) => name === 'peers',
      );
      return group?.institutions || [];
    },
    [getProduct],
  );

  const peersInfo = useCallback(
    (productName: string) => ({
      supportsPeers: false,
      supportsChangingPeers: false,
      peerChangeOpen: false,
      lastPeerChangeDate: null,
      maxPeerCount: -1,
      peerChangeIntervalWeeks: null,
      ...getProduct(productName)?.peersInfo,
    }),
    [getProduct],
  );

  return (
    <ProductsContext.Provider
      value={{
        products,
        productPeers,
        productGroups,
        peersInfo,
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
};

export default Products;
